import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { isHideOnlyGlMap } from '../../uiModels/glMapOperations/helpers'
import { useOperationsContext } from '../../workspace/operationsContext'
import { configurationItemsMapToList } from '../errorsPanel/helpers'
import { groupSearchResults } from './helpers'
import type {
  ConfigurationItemSearchFunctions,
  ConfigurationItemSearchResult,
} from './types'

/**
 * Searches GL maps by their mapping ID. GL-map entries are only authored in Excel and
 * are not part of the configuration model, so no class, property or F+ search exists
 * for them.
 */
export const useGlMapSearch = (): ConfigurationItemSearchFunctions => {
  const { glMapOperations } = useOperationsContext()
  const translate = useTranslate()

  const allGlMaps = useMemo(
    () =>
      configurationItemsMapToList(glMapOperations)
        .map((indexedGlMap) => ({
          ...indexedGlMap,
          operations: indexedGlMap.operations.filter(
            (operation) => !operation.annulOperations.length,
          ),
        }))
        .filter(({ operations }) => operations.length),
    [glMapOperations],
  )

  const searchByName = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allGlMaps
          .filter(({ mappingId }) => mappingId.toLowerCase().includes(needle))
          .map(({ mappingId, operations, isDraft }) => {
            const latestOperation = operations.at(-1)

            assertNotEmpty(latestOperation)

            return {
              value: {
                id: mappingId,
                type: null,
                operationType: isHideOnlyGlMap(operations)
                  ? 'hideGlMap'
                  : 'glMap',
                packageName: translate('glMapsTree.fallbackPackage.glMaps'),
                block: latestOperation.block,
                layer: latestOperation.layer,
                isDraft,
              },
            } satisfies ConfigurationItemSearchResult
          }),
      )
    },
    [allGlMaps, translate],
  )

  const searchByClass = useCallback(() => {
    throw new Error('Searching GL maps by class is unavailable')
  }, [])

  const searchByFPlus = useCallback(() => {
    throw new Error('Searching FPlus within GL maps is unavailable')
  }, [])

  const searchByProperties = useCallback(() => {
    throw new Error('Searching GL maps by properties is unavailable')
  }, [])

  const searchAll = useCallback(
    async (query: string) => {
      const allByName = await searchByName(query)

      return groupSearchResults(...allByName)
    },
    [searchByName],
  )

  return {
    name: searchByName,
    className: searchByClass,
    fplus: searchByFPlus,
    properties: searchByProperties,
    all: searchAll,
  }
}
